import { KeyRound, Loader2, UserCog } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GlobalRoleSelect } from '@/components/admin/GlobalRoleSelect'
import { PasswordInput } from '@/components/custom-ui/PasswordInput'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import type { GlobalRole } from '@/services/api/auth'
import type { UserInfo } from '@/services/api/lake'

export interface EditUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: UserInfo | null
  onUpdateUsername: (username: string) => void
  onUpdateRole: (role: GlobalRole) => void
  /** Omitted when passwords are IdP-managed (SSO) — the reset section is then hidden. */
  onResetPassword?: (password: string) => void
  savingUsername: boolean
  savingRole: boolean
  savingPassword: boolean
}

export function EditUserDialog({
  open,
  onOpenChange,
  user,
  onUpdateUsername,
  onUpdateRole,
  onResetPassword,
  savingUsername,
  savingRole,
  savingPassword,
}: EditUserDialogProps) {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [role, setRole] = useState<GlobalRole>('user')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  // Seed the username/role fields when the dialog opens for a user.
  useEffect(() => {
    if (open && user) {
      setUsername(user.username)
      setRole(user.role)
      setPassword('')
      setConfirm('')
    }
  }, [open, user])

  const usernameUnchanged = username.trim().toLowerCase() === user?.username
  const mismatch = !!confirm && password !== confirm
  const canResetPassword = password.length >= 6 && !mismatch && !savingPassword

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 max-md:flex-wrap">
            <UserCog className="h-4 w-4 shrink-0 text-muted-foreground" />
            {t('admin.users.editUser')}
            <span className="text-muted-foreground font-normal max-md:min-w-0 max-md:break-all">
              — {user?.username}
            </span>
          </DialogTitle>
          <DialogDescription>{t('admin.users.editUserDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t('admin.users.changeUsername')}</Label>
            <div className="flex gap-2">
              <Input
                placeholder={t('admin.users.newUsernamePlaceholder')}
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={() => onUpdateUsername(username)}
                disabled={!username.trim() || usernameUnchanged || savingUsername}
                variant="outline"
                size="sm"
                className="h-9 px-4 shrink-0 md:h-10"
              >
                {savingUsername ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  t('common.save')
                )}
              </Button>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <Label className="text-sm font-medium">{t('admin.users.changeRole')}</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <GlobalRoleSelect value={role} onChange={setRole} />
              </div>
              <Button
                onClick={() => onUpdateRole(role)}
                disabled={role === user?.role || savingRole}
                variant="outline"
                size="sm"
                className="h-9 px-4 shrink-0 md:h-10"
              >
                {savingRole ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('common.save')}
              </Button>
            </div>
          </div>

          {onResetPassword && (
            <>
              <Separator />

              <div className="space-y-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                  {t('admin.users.resetPassword')}
                </Label>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">{t('admin.users.newPassword')}</Label>
                    <PasswordInput
                      placeholder={t('common.minCharacters')}
                      value={password}
                      onChange={setPassword}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t('admin.users.confirmNewPassword')}</Label>
                    <PasswordInput
                      placeholder={t('common.repeatPassword')}
                      value={confirm}
                      onChange={setConfirm}
                      invalid={mismatch}
                    />
                    {mismatch && (
                      <p className="text-xs text-destructive">{t('common.passwordsDoNotMatch')}</p>
                    )}
                  </div>
                  <Button
                    onClick={() => onResetPassword(password)}
                    disabled={!canResetPassword}
                    className="w-full"
                  >
                    {savingPassword ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <KeyRound className="h-4 w-4 mr-2" />
                    )}
                    {t('admin.users.setPassword')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Below md the dialog covers nearly the whole screen (hardly any overlay left to tap), so
            it gets its own way out. Sticky and opaque: the form scrolls underneath it. */}
        <DialogFooter className="sticky -bottom-4 bg-muted md:hidden">
          <DialogClose asChild>
            <Button variant="outline">{t('common.close')}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
